<script setup>
  import {computed, inject, onBeforeUnmount, ref} from "vue";

  import ComponentAiRating from "@components/AiRating.vue";
  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDoNotShowAgain from "@components/Checkboxes/DoNotShowAgain.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedInfoButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {extractAiErrorMessage} from "@utils/Helpers/AiErrorMessage.js";
  import {replaceLatexSymbols} from "@utils/LatexSymbols.js";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";
  import {useSseStream} from "@/resources/js/Composable/useSseStream.js";

  defineOptions({
    name: "ComponentAiSummaryDialog",
  });

  const props = defineProps({
    target: {
      type: String,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
  });

  const GENERIC_ERROR_MESSAGE =
    "Es ist ein Fehler bei der Zusammenfassung aufgetreten. Bitte versuchen Sie es später erneut.";

  const emit = defineEmits(["closed"]);

  const privacy = inject("$privacy");
  const refSummaryDialog = ref(null);
  const refAiConfirmationDialog = ref(null);

  const isLoading = ref(false);
  const error = ref(null);

  const rating = ref(null);
  const ratingReason = ref(null);

  // output properties
  const resultText = ref(null);

  // escapeHtml escapes only the <, > that \lt / \gt produce, so they're safe in the v-html / Trix
  const enhancedText = computed(() => replaceLatexSymbols(resultText.value, {escapeHtml: true}));

  let stream = null;

  const generate = async () => {
    // abort a stream that's still running (e.g. "Neu generieren" while loading)
    stream?.close();

    updateResponse(null);

    rating.value = null;
    ratingReason.value = null;
    resultText.value = "";
    error.value = null;

    isLoading.value = true;

    const url = route("api.record.summarize-claims", {
      target: props.target,
      patient: props.patient.id,
      record: props.record.id,
    });

    stream = useSseStream(url, {
      onDelta: (delta) => (resultText.value += delta),
      onEnd: () => (isLoading.value = false),
      onError: ({data}) => {
        isLoading.value = false;
        resultText.value = null;

        error.value = extractAiErrorMessage(data, GENERIC_ERROR_MESSAGE);
      },
    });
  };

  onBeforeUnmount(() => stream?.close());

  const updateResponse = (actionValue) => {
    if (!resultText.value || (!actionValue && !rating.value)) {
      // nothing to update - skip
      return;
    }

    useAuthHttp({
      rating: rating.value,
      ratingReason: ratingReason.value,
      action: actionValue,
    }).post(
      route("api.record.summarize-claims.update", {
        target: props.target,
        patient: props.patient.id,
        record: props.record.id,
      }),
    );
  };

  const open = (force) => {
    let aiBasedSummaryWarning = privacy.getConfiguration("aiBasedSummaryWarning");

    if (force === true || aiBasedSummaryWarning?.amount === false) {
      refSummaryDialog.value.open();
      generate();
    } else {
      // show warning, when not explicitly hidden via users configuration
      refAiConfirmationDialog.value.open();
    }
  };

  const apply = () => {
    const key = props.target === "doctor" ? "doctorsummary" : "patientsummary";
    const form = useForm({[key]: enhancedText.value});

    form.patch(
      route("records.update", {
        patient: props.patient.id,
        record: props.record.id,
      }),
      {
        preserveScroll: true,
        only: ["flash", "errors", "record"],
      },
    );

    close(true);
  };

  const close = (wasAccepted = false) => {
    const wasLoading = isLoading.value;

    stream?.close();
    isLoading.value = false;

    // cancelling mid-load: nothing was generated yet, so there's nothing to tell the backend
    if (wasAccepted || !wasLoading) {
      updateResponse(wasAccepted ? "accept" : "abort");
    }

    refSummaryDialog.value.close();
    emit("closed");
  };

  defineExpose({
    open,
  });
</script>

<template>
  <ComponentConfirmationDialog
    ref="refAiConfirmationDialog"
    title="KI-unterstützte Erstellung einer Zusammenfassung starten?"
    primary-label="Verstanden"
    dialog-width="medium"
    @confirmed="open(true)"
  >
    <template #content>
      <p>
        Mit Hilfe dieser KI-Funktion wird aus den von Ihnen ausgewählten MediCheck-Ergebnissen eine kompakte
        Zusammenfassung erstellt. Der Schutz Ihrer Angaben und der darin enthaltenen Informationen ist jederzeit
        gewährleistet.
      </p>
      <p class="mt-4">
        Bitte beachten Sie, dass jede KI Fehler machen oder unvollständige Angaben erzeugen kann. Überprüfen Sie daher
        alle Fakten und wichtigen Informationen sorgfältig.
      </p>
      <component-do-not-show-again class="mt-4" settings-name="aiBasedSummaryWarning" />
    </template>
  </ComponentConfirmationDialog>

  <ComponentDialog ref="refSummaryDialog" dialog-width="large" :test-id="getTestId('patient-summary-dialog')">
    <template #content>
      <h1 class="mb-3 text-2xl">
        KI-Zusammenfassung für den {{ target === "doctor" ? "Arztbericht" : "Patientenbericht" }}
        <MedInfoButton
          size="lg"
          side="top"
          aria-label="KI-Zusammenfassung Info"
          disable-portal
          :data-test="getTestId('ai-summary-info-button')"
        >
          <p class="text-sm">
            Mit Hilfe dieser KI-Funktion wird aus den von Ihnen ausgewählten MediCheck-Ergebnissen eine kompakte
            Zusammenfassung erstellt. Der Schutz Ihrer Angaben und der darin enthaltenen Informationen ist jederzeit
            gewährleistet.
          </p>
          <p class="mt-4 text-sm">
            Bitte beachten Sie, dass jede KI Fehler machen oder unvollständige Angaben erzeugen kann. Überprüfen Sie
            daher alle Fakten und wichtigen Informationen sorgfältig.
          </p>
        </MedInfoButton>
      </h1>

      <template v-if="isLoading && !resultText">
        <div class="my-10 flex flex-col items-center justify-center gap-5">
          <MedSpinner size="3xl" :data-test="getTestId('ai-summary-dialog-spinner')" />

          <p class="text-center">
            Einen kurzen Augenblick noch…
            <br />
            Wir bringen alles Wichtige für Sie auf den Punkt.
          </p>
        </div>
      </template>
      <template v-else-if="error">
        <ComponentAlert type="error">
          {{ error }}
        </ComponentAlert>
      </template>

      <template v-else-if="resultText">
        <!-- eslint-disable-next-line vue/no-v-html -- the summarizer prompt mandates HTML output (<b>, <u>, <br>), see ai-client claim-summarizer/*-system.blade.php -->
        <div class="py-1" v-html="enhancedText" />

        <p class="text-sm text-gray-500">
          KI-generierte Inhalte können Fehler enthalten. Bitte überprüfen Sie wichtige Informationen.
        </p>

        <div class="mt-3">
          <MedButton
            icon="cached"
            :data-test="getTestId('patient-summary-dialog-generate-button')"
            :disabled="isLoading"
            @click="generate"
          >
            Neu generieren
          </MedButton>

          <ComponentAiRating
            v-model:rating="rating"
            v-model:reason="ratingReason"
            label="War diese KI generierte Zusammenfassung hilfreich für Sie?"
            :disabled="isLoading"
          />
        </div>
      </template>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :is-loading="isLoading"
        :data-test="getTestId('patient-summary-dialog-apply-button')"
        :disabled="!resultText"
        @click="apply"
      >
        Übernehmen
      </MedButton>

      <MedButton :data-test="getTestId('patient-summary-dialog-cancel-button')" @click="() => close()">
        Abbrechen
      </MedButton>
    </template>
  </ComponentDialog>
</template>
